import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { ApplicationZodSchema } from "./useGetAllApplications";
import { z } from "zod";

export const oneApplicationKey = "getOneApplication" as const;

const jsonLdHeaders = {
    "Content-Type": "application/ld+json",
    Accept: "application/ld+json",
};

type Options = {
    cacheTime?: number;
    enabled?: boolean;
};

// Define error type for better type safety
interface ApiErrorResponse {
    response?: {
        status: number;
        data?: unknown;
    };
    message: string;
}

export const useGetOneApplication = (applicationId: string | undefined, options?: Options) => {
    const { cacheTime = 0, enabled = true } = options || {};

    const query = useQuery({
        queryKey: [oneApplicationKey, applicationId],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled: !!applicationId && enabled,
        queryFn: async () => {
            if (!applicationId) {
                throw new Error("Application ID is required");
            }

            const url = `/api/integrations/applications/${applicationId}`;

            return loggedInApiCall.get(url, {
                headers: jsonLdHeaders,
            });
        },
        select: (response) => {
            const application = ApplicationZodSchema.parse(response.data);
            return application;
        },
        retry: (failureCount, error: ApiErrorResponse) => {
            if (error?.response?.status === 404) {
                return false;
            }
            return failureCount < 2;
        },
        onError: (error: ApiErrorResponse) => {
            if (error?.response?.status === 404) {
                console.error(`Application with ID ${applicationId} not found`);
            } else {
                console.error(`Error fetching application ${applicationId}:`, error);
            }
        },
    });

    return {
        application: query.data,
        ...query,
    };
};

export interface ExtendedWebhook {
    id: string;
    active: boolean;
    application: string;
    name?: string;
    method?: string;
    headers?: Record<string, string>;
    retryCount?: number;
    endpoint?: string;
    eventName?: string;
}

export const ExtendedWebhookSchema = z.object({
    id: z.string(),
    active: z.boolean(),
    application: z.string(),
    name: z.string().optional(),
    method: z.string().optional(),
    headers: z
        .union([z.record(z.string()), z.array(z.any())])
        .transform((val) => {
            if (Array.isArray(val)) {
                return {};
            }
            return val;
        })
        .optional(),
    retryCount: z.number().optional(),
    endpoint: z.string().optional(),
    eventName: z.string().optional(),
});

export const useGetApplicationWebhooks = (applicationId: string | undefined, options?: Options) => {
    const { cacheTime = 0, enabled = true } = options || {};

    const query = useQuery({
        queryKey: ["applicationWebhooks", applicationId],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled: !!applicationId && enabled,
        queryFn: async () => {
            if (!applicationId) {
                throw new Error("Application ID is required");
            }

            const url = `/api/integrations/applications/${applicationId}/webhooks`;

            return loggedInApiCall.get(url, {
                headers: jsonLdHeaders,
            });
        },
        select: (response) => {
            const rawData = response.data["hydra:member"] || response.data;

            if (Array.isArray(rawData) && rawData.length > 0 && typeof rawData[0] === "string") {
                return z.array(ExtendedWebhookSchema).parse(
                    rawData.map((id) => ({
                        id: id,
                        active: false,
                        application: `/api/integrations/applications/${applicationId}`,
                    })),
                );
            }

            return z.array(ExtendedWebhookSchema).parse(rawData);
        },
        retry: (failureCount, error: ApiErrorResponse) => {
            if (error?.response?.status === 404) {
                return false;
            }
            return failureCount < 2;
        },
    });

    return {
        webhooks: query.data || [],
        ...query,
    };
};
